import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { AuthApiService } from '../../shared/api/auth-api.service';
import { AdminUserDto, RoleCatalogItemDto } from '../../shared/api/auth-api.types';
import {
  UserProfileApiService,
  UserProfileDto,
  UserStatsDto
} from '../../shared/api/user-profile-api.service';
import { NotificationPreferencesApiService } from '../../shared/api/notification-preferences-api.service';
import {
  NotificationPreferencesDto,
  UpdateNotificationPreferencesRequest
} from '../../shared/api/notification-preferences-api.types';
import { AuthService } from '../../shared/auth/auth.service';
import { LoaderComponent } from '../../shared/ui/loader/loader.component';

const defaultNotificationPrefs: NotificationPreferencesDto = {
  emailNotifications: true,
  ideaStatusChanges: true,
  newComments: true,
  followedIdeaUpdates: true,
  challengeAnnouncements: true,
  weeklyDigest: false
};

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, LoaderComponent],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.css'
})
export class SettingsComponent implements OnInit, OnDestroy {
  loading = true;
  saving = false;
  savingNotificationPrefs = false;
  errorMessage = '';
  successMessage = '';

  displayName = '';
  email = '';
  departmentId: string | null | '' = '';

  departments: { id: string; name: string }[] = [];

  stats: UserStatsDto = { ideasSubmitted: 0, totalPoints: 0 };

  emailNotifications = true;
  ideaStatusChanges = true;
  newComments = true;
  followedIdeaUpdates = true;
  challengeAnnouncements = true;
  weeklyDigest = false;

  /** Admin: user permissions */
  adminUsers: AdminUserDto[] = [];
  roleCatalog: RoleCatalogItemDto[] = [];
  /** userId → selected role codes (uppercase) */
  roleDrafts: Record<string, string[]> = {};
  savingRolesForUserId: string | null = null;
  adminSectionError = '';
  adminSectionSuccess = '';

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly profileApi: UserProfileApiService,
    private readonly notificationPrefsApi: NotificationPreferencesApiService,
    private readonly authApi: AuthApiService,
    private readonly auth: AuthService
  ) {}

  ngOnInit(): void {
    const base$ = forkJoin({
      profile: this.profileApi.getMyProfile(),
      departments: this.profileApi.getDepartments(),
      notificationPrefs: this.notificationPrefsApi.get().pipe(catchError(() => of(null))),
      stats: this.profileApi.getMyStats().pipe(catchError(() => of({ ideasSubmitted: 0, totalPoints: 0 })))
    });

    const admin$ =
      this.auth.isAdmin
        ? forkJoin({
            users: this.authApi.getAdminUsers(),
            roles: this.authApi.getAdminRoleCatalog()
          }).pipe(catchError(() => of({ users: [] as AdminUserDto[], roles: [] as RoleCatalogItemDto[] })))
        : of({ users: [] as AdminUserDto[], roles: [] as RoleCatalogItemDto[] });

    forkJoin({ base: base$, admin: admin$ })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ base, admin }) => {
          this.applyProfile(base.profile);
          this.departments = base.departments ?? [];
          this.applyNotificationPrefs(base.notificationPrefs);
          this.stats = base.stats ?? { ideasSubmitted: 0, totalPoints: 0 };
          this.adminUsers = admin.users ?? [];
          this.roleCatalog = (admin.roles ?? [])
            .filter((r) => (r.code ?? '').toUpperCase() !== 'USER')
            .slice()
            .sort((a, b) => a.code.localeCompare(b.code));
          this.roleDrafts = {};
          for (const u of this.adminUsers) {
            this.roleDrafts[u.userId] = (u.roles ?? [])
              .map((r) => r.toUpperCase())
              .filter((c) => c !== 'USER');
          }
          this.loading = false;
        },
        error: () => {
          this.errorMessage = 'Could not load your settings. Please refresh or sign in again.';
          this.loading = false;
        }
      });
  }

  get isAdmin(): boolean {
    return this.auth.isAdmin;
  }

  hasRoleDraft(userId: string, roleCode: string): boolean {
    const set = this.roleDrafts[userId];
    return set?.includes(roleCode.toUpperCase()) ?? false;
  }

  toggleUserRole(userId: string, roleCode: string, checked: boolean): void {
    const code = roleCode.toUpperCase();
    const list = this.roleDrafts[userId] ?? [];
    this.roleDrafts[userId] = checked
      ? list.includes(code)
        ? list
        : [...list, code]
      : list.filter((c) => c !== code);
  }

  saveUserRoles(userId: string): void {
    const codes = this.roleDrafts[userId] ?? [];
    if (codes.length === 0) {
      this.adminSectionError = 'Each user must keep at least one role.';
      this.adminSectionSuccess = '';
      return;
    }

    this.savingRolesForUserId = userId;
    this.adminSectionError = '';
    this.adminSectionSuccess = '';

    this.authApi
      .updateUserRoles(userId, { roleCodes: codes })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.savingRolesForUserId = null;
          const u = this.adminUsers.find((x) => x.userId === res.userId);
          if (u) {
            u.roles = Array.isArray(res.roleCodes) ? [...res.roleCodes] : codes;
          }
          this.roleDrafts[userId] = (res.roleCodes ?? codes).map((c) => c.toUpperCase());
          this.adminSectionSuccess = 'Permissions updated.';
          const me = this.auth.currentUser;
          if (me?.userId === userId) {
            void this.auth.refreshCurrentUser().pipe(takeUntil(this.destroy$)).subscribe();
          }
        },
        error: (err: unknown) => {
          this.savingRolesForUserId = null;
          this.adminSectionSuccess = '';
          this.adminSectionError = this.formatRoleSaveError(err);
        }
      });
  }

  private formatRoleSaveError(err: unknown): string {
    if (!(err instanceof HttpErrorResponse) || !err.error || typeof err.error !== 'object') {
      return 'Could not update permissions. Try again.';
    }
    const body = err.error as Record<string, unknown>;
    const errors = body['errors'];
    if (errors && typeof errors === 'object') {
      for (const val of Object.values(errors as Record<string, unknown>)) {
        if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'string') {
          return val[0];
        }
      }
    }
    const detail = body['detail'];
    if (typeof detail === 'string' && detail.trim()) {
      return detail.trim();
    }
    if (err.status === 403) {
      return 'You need the Admin role to change permissions.';
    }
    return 'Could not update permissions.';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  save(): void {
    const name = (this.displayName ?? '').trim();
    if (!name) {
      this.errorMessage = 'Full name is required.';
      this.successMessage = '';
      return;
    }

    this.saving = true;
    this.errorMessage = '';
    this.successMessage = '';

    const dept = (this.departmentId ?? '').trim();
    const body = {
      displayName: name,
      departmentId: dept === '' ? null : dept
    };

    this.profileApi
      .updateMyProfile(body)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (profile) => {
          this.applyProfile(profile);
          this.saving = false;
          this.successMessage = 'Your profile was saved.';
          void this.auth.refreshCurrentUser().pipe(takeUntil(this.destroy$)).subscribe();
        },
        error: (err: unknown) => {
          this.saving = false;
          this.successMessage = '';
          this.errorMessage = this.formatSaveError(err);
        }
      });
  }

  private notificationPayload(): UpdateNotificationPreferencesRequest {
    return {
      emailNotifications: this.emailNotifications,
      ideaStatusChanges: this.ideaStatusChanges,
      newComments: this.newComments,
      followedIdeaUpdates: this.followedIdeaUpdates,
      challengeAnnouncements: this.challengeAnnouncements,
      weeklyDigest: this.weeklyDigest
    };
  }

  private persistNotificationPrefs(): void {
    if (this.savingNotificationPrefs) return;
    this.savingNotificationPrefs = true;
    this.notificationPrefsApi
      .update(this.notificationPayload())
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of(null))
      )
      .subscribe({
        next: (prefs) => {
          if (prefs) {
            this.applyNotificationPrefs(prefs);
          }
          this.savingNotificationPrefs = false;
        },
        error: () => {
          this.savingNotificationPrefs = false;
        }
      });
  }

  toggleEmailNotifications(): void {
    this.emailNotifications = !this.emailNotifications;
    this.persistNotificationPrefs();
  }

  toggleIdeaStatusChanges(): void {
    this.ideaStatusChanges = !this.ideaStatusChanges;
    this.persistNotificationPrefs();
  }

  toggleNewComments(): void {
    this.newComments = !this.newComments;
    this.persistNotificationPrefs();
  }

  toggleFollowedIdeaUpdates(): void {
    this.followedIdeaUpdates = !this.followedIdeaUpdates;
    this.persistNotificationPrefs();
  }

  toggleChallengeAnnouncements(): void {
    this.challengeAnnouncements = !this.challengeAnnouncements;
    this.persistNotificationPrefs();
  }

  toggleWeeklyDigest(): void {
    this.weeklyDigest = !this.weeklyDigest;
    this.persistNotificationPrefs();
  }

  private applyProfile(profile: UserProfileDto): void {
    this.displayName = profile.displayName ?? '';
    this.email = profile.email ?? '';
    this.departmentId = profile.departmentId ?? '';
  }

  private applyNotificationPrefs(prefs: NotificationPreferencesDto | null): void {
    const p = prefs ?? defaultNotificationPrefs;
    this.emailNotifications = p.emailNotifications ?? defaultNotificationPrefs.emailNotifications;
    this.ideaStatusChanges = p.ideaStatusChanges ?? defaultNotificationPrefs.ideaStatusChanges;
    this.newComments = p.newComments ?? defaultNotificationPrefs.newComments;
    this.followedIdeaUpdates = p.followedIdeaUpdates ?? defaultNotificationPrefs.followedIdeaUpdates;
    this.challengeAnnouncements = p.challengeAnnouncements ?? defaultNotificationPrefs.challengeAnnouncements;
    this.weeklyDigest = p.weeklyDigest ?? defaultNotificationPrefs.weeklyDigest;
  }

  private formatSaveError(err: unknown): string {
    if (!(err instanceof HttpErrorResponse) || !err.error || typeof err.error !== 'object') {
      return 'Could not save changes. Please try again.';
    }
    const body = err.error as Record<string, unknown>;
    const errors = body['errors'];
    if (errors && typeof errors === 'object') {
      for (const val of Object.values(errors as Record<string, unknown>)) {
        if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'string') {
          return val[0];
        }
      }
    }
    const detail = body['detail'];
    if (typeof detail === 'string' && detail.trim()) {
      return detail.trim();
    }
    return 'Could not save changes. Please check your input and try again.';
  }
}
